import type { FlightOptionModel } from '@elara/client-core/models/FlightOptionModel';
import type { GoogleFlightBookingOptionModel } from '@elara/client-core/models/GoogleFlightBookingOptionModel';
import type { FlightBookingOptionsState } from '@elara/client-core/states/FlightBookingOptionsState';
import type { BookingLeg } from '@elara/domain/models/schemas/flight-schemas';

import { useEffect, useState } from 'react';

import { findClosestBookingMatch } from '@elara/client-core/lib/flight-booking-options';
import { cn, formatPrice, pluralize } from '@elara/client-core/lib/utils';
import { createGoogleFlightBookingOptionModel } from '@elara/client-core/models/GoogleFlightBookingOptionModel';

import { useFlightBookingOptionsState, useUserProfileState } from '#states/hooks.js';
import { AppImage, Badge, IconSizes, MaterialIcon } from '#ui';
import { Alert, AlertDescription } from '#ui/alert.js';
import { Button } from '#ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '#ui/dialog.js';

const INITIAL_BOOKING_OPTIONS_LIMIT = 10;

/**
 * Submits a POST request to the given URL with postData (application/x-www-form-urlencoded)
 * by creating a temporary form and opening the response in a new tab.
 */
function openPostInNewTab(url: string, postData: string): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = url;
  form.target = '_blank';
  form.rel = 'noopener noreferrer';

  if (postData) {
    for (const pair of postData.split('&')) {
      const eq = pair.indexOf('=');
      const name = eq === -1 ? pair : decodeURIComponent(pair.slice(0, eq).replaceAll('+', ' '));
      const value = eq === -1 ? '' : decodeURIComponent(pair.slice(eq + 1).replaceAll('+', ' '));
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.append(input);
    }
  }

  document.body.append(form);
  form.submit();
  form.remove();
}

function ProviderLogo({ logo, name, className }: { logo?: string; name: string; className?: string }) {
  if (logo?.trim()) {
    return <AppImage src={logo} alt={name} className={cn('h-4 @xl:h-10 w-auto object-contain', className)} />;
  }
  return (
    <div className={cn('h-4 @xl:h-10 w-4 @xl:w-10 flex items-center justify-center', className)}>
      <MaterialIcon icon="flight" size={IconSizes.sm} className="text-muted-foreground" />
    </div>
  );
}

function BookingOptionRow({
  bookingOption,
  flightOption,
  isMatched,
  onProviderClicked,
  onBookOverride,
}: {
  bookingOption: GoogleFlightBookingOptionModel;
  flightOption: FlightOptionModel;
  isMatched?: boolean;
  onProviderClicked?: (providerName: string) => void;
  /**
   * Replaces the Book button's default behaviour (opening the provider's page
   * in a new tab, or showing the separate-tickets dialog). The flight guide
   * uses this to send users into the dedicated flight checkout page first.
   */
  onBookOverride?: () => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const userProfileState = useUserProfileState();
  const preferredCurrency = userProfileState.profile?.preferredCurrency;
  const leg = bookingOption.legs[0];
  if (!leg) return null;

  const isSeparate = bookingOption.isSeparateTickets;

  return (
    <li
      className={cn(
        'grid items-center grid-rows-[auto_auto] @xl:grid-rows-1 gap-2 gap-y-4 @xl:gap-4 border-b last:border-b-0 p-4 text-card-foreground',
        bookingOption.isSeparateTickets
          ? 'grid-cols-[40px_auto] @xl:grid-cols-[88px_1fr_auto]'
          : 'grid-cols-[16px_auto] @xl:grid-cols-[40px_1fr_auto]',
      )}
    >
      <div className="row-start-1 col-span-1 flex items-center gap-2">
        {bookingOption.legs.map((l) => (
          <ProviderLogo key={l.providerName} logo={l.providerLogo} name={l.providerName} />
        ))}
      </div>

      <div className="flex items-baseline gap-2 @xl:items-start @xl:gap-0 @xl:flex-col row-start-1 col-span-1">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <div className="font-medium text-sm @xl:text-base">{bookingOption.providerName}</div>
            {bookingOption.legs[0]?.airline && (
              <Badge variant="outline" size="sm" className="text-muted-foreground">
                Airline
              </Badge>
            )}
          </div>
          {isSeparate && (
            <div className="text-xs text-muted-foreground">
              {bookingOption.legs.length} {pluralize(bookingOption.legs.length, 'ticket')} must be booked separately
            </div>
          )}
        </div>
        {!isSeparate && bookingOption.title != null && bookingOption.title !== bookingOption.providerName && (
          <div className="text-muted-foreground text-sm @xl:text-xs">{bookingOption.title}</div>
        )}
      </div>

      <div className="flex items-center justify-between gap-4 row-start-2 col-span-2 @xl:col-span-1 @xl:row-start-1 @xl:justify-end">
        <div className="flex flex-col @xl:items-end @xl:text-right">
          <div className="font-medium text-sm @xl:text-base">
            {bookingOption.formattedPrice(flightOption.totalPaidPassengers, preferredCurrency)}
          </div>
          <div className="text-muted-foreground text-xs">
            {bookingOption.costDescription(flightOption.totalPaidPassengers, preferredCurrency)}
          </div>
        </div>

        {onBookOverride ? (
          <Button type="button" variant={isMatched ? 'default' : 'outline'} onClick={onBookOverride}>
            <MaterialIcon icon="shopping_cart_checkout" />
            Book flight
          </Button>
        ) : isSeparate ? (
          <>
            <Button type="button" variant={isMatched ? 'default' : 'outline'} onClick={() => setDialogOpen(true)}>
              Book flights
            </Button>
            <SeparateTicketsDialog
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              bookingOption={bookingOption}
              flightOption={flightOption}
              preferredCurrency={preferredCurrency}
              onProviderClicked={onProviderClicked}
            />
          </>
        ) : (
          <Button
            type="button"
            variant={isMatched ? 'default' : 'outline'}
            onClick={() => {
              openPostInNewTab(leg.bookingRequest.url, leg.bookingRequest.postData);
              onProviderClicked?.(bookingOption.providerName);
            }}
          >
            Book flight ↗
          </Button>
        )}
      </div>
    </li>
  );
}

function LegRow({
  leg,
  totalPaidPassengers,
  preferredCurrency,
  isMatched = false,
  onProviderClicked,
}: {
  leg: BookingLeg;
  totalPaidPassengers: number;
  preferredCurrency?: string;
  isMatched?: boolean;
  onProviderClicked?: (providerName: string) => void;
}) {
  let cost = leg.cost;
  if (preferredCurrency) {
    const localCost = leg.localCosts?.find((lc) => lc.currency.toLowerCase() === preferredCurrency.toLowerCase());
    if (localCost) cost = localCost;
  }

  const label = leg.direction === 'departing' ? 'Departing flight' : 'Returning flight';

  return (
    <li>
      <div className="text-sm font-medium mb-4">{label}</div>
      <div className="grid items-center grid-cols-[24px_1fr_auto] @lg:grid-cols-[40px_1fr_auto] gap-2 @lg:gap-4 border-t p-3 border rounded-md">
        <ProviderLogo logo={leg.providerLogo} name={leg.providerName} className="h-4 @lg:h-10" />

        <div className="flex flex-col min-w-0">
          <span className="text-sm font-medium truncate">{leg.providerName}</span>
          <div className="text-xs text-muted-foreground">{leg.flightNumbers?.join(', ')}</div>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-sm font-medium">{formatPrice(cost.amount / totalPaidPassengers, cost.currency)}</div>
            <div className="text-xs text-muted-foreground">
              {totalPaidPassengers > 1
                ? `${formatPrice(cost.amount, cost.currency)} for all passengers`
                : `Per passenger`}
            </div>
          </div>
          <Button
            type="button"
            variant={isMatched ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              openPostInNewTab(leg.bookingRequest.url, leg.bookingRequest.postData);
              onProviderClicked?.(leg.providerName);
            }}
          >
            Book
          </Button>
        </div>
      </div>
    </li>
  );
}

function SeparateTicketsDialog({
  open,
  onOpenChange,
  bookingOption,
  flightOption,
  preferredCurrency,
  onProviderClicked,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingOption: GoogleFlightBookingOptionModel;
  flightOption: FlightOptionModel;
  preferredCurrency?: string;
  onProviderClicked?: (providerName: string) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl @container">
        <DialogHeader>
          <DialogTitle>Book separate tickets</DialogTitle>
          <DialogDescription className="text-pretty">
            This flight requires booking each ticket separately. Book each ticket one after the other.
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-4">
          {bookingOption.legs.map((leg) => (
            <LegRow
              key={leg.direction}
              leg={leg}
              totalPaidPassengers={flightOption.totalPaidPassengers}
              preferredCurrency={preferredCurrency}
              isMatched={true}
              onProviderClicked={onProviderClicked}
            />
          ))}
        </ul>

        <div className="flex items-start justify-between text-sm">
          <span className="font-medium">Total price, incl. taxes &amp; fees</span>
          <div className="flex flex-col items-end">
            <div className="font-medium">
              {bookingOption.formattedPrice(flightOption.totalPaidPassengers, preferredCurrency)}
            </div>
            <div className="text-xs text-muted-foreground">
              {bookingOption.costDescription(flightOption.totalPaidPassengers, preferredCurrency)}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BookingOptionSkeletonRow() {
  return (
    <div className="grid items-center grid-cols-[16px_auto] @xl:grid-cols-[40px_1fr_auto] gap-2 @xl:gap-4 border-b last:border-b-0 p-4">
      <div className="h-4 w-4 @xl:h-10 @xl:w-10 rounded bg-muted animate-pulse" />
      <div className="flex flex-col gap-1.5">
        <div className="h-4 w-28 rounded bg-muted animate-pulse" />
        <div className="h-3 w-20 rounded bg-muted animate-pulse" />
      </div>
      <div className="hidden @xl:flex items-center gap-4">
        <div className="flex flex-col items-end gap-1.5">
          <div className="h-4 w-16 rounded bg-muted animate-pulse" />
          <div className="h-3 w-24 rounded bg-muted animate-pulse" />
        </div>
        <div className="h-9 w-24 rounded-md bg-muted animate-pulse" />
      </div>
    </div>
  );
}

function BookingOptionsSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-3 @container">
      <div className="rounded-md border bg-background">
        {Array.from({ length: rows }, (_, i) => (
          <BookingOptionSkeletonRow key={i} />
        ))}
      </div>
    </div>
  );
}

export interface GoogleFlightBookingOptionsProps {
  flightOption: FlightOptionModel;
  /** Fires when the user clicks any provider's Book button (single or separate-tickets). */
  onProviderClicked?: (providerName: string) => void;
  /**
   * When true, render only the matched (closest-priced) option as a single-row
   * summary — used by the flight guide to show "what's the price and who's
   * providing it" without the full provider list.
   */
  matchedOnly?: boolean;
  /**
   * Replaces each row's Book-button behaviour with a custom handler (used by
   * the flight guide to route users into the dedicated checkout page first
   * rather than opening the provider tab directly).
   */
  onBookOverride?: () => void;
}

export function GoogleFlightBookingOptions({
  flightOption,
  onProviderClicked,
  matchedOnly = false,
  onBookOverride,
}: GoogleFlightBookingOptionsProps) {
  const bookingState: FlightBookingOptionsState = useFlightBookingOptionsState();
  const userProfileState = useUserProfileState();
  const { bookingToken } = flightOption;
  const [showAll, setShowAll] = useState(false);

  const profile = userProfileState.profile;
  const bookingOverrides =
    profile?.preferredCurrency != null || profile?.countryCode != null
      ? {
          ...(profile?.preferredCurrency != null && { currency: profile.preferredCurrency }),
          ...(profile?.countryCode != null && { countryCode: profile.countryCode }),
        }
      : undefined;

  useEffect(() => {
    if (!bookingToken) return;
    setShowAll(false);
    bookingState.reset();
    void bookingState.loadBookingOptions(flightOption, bookingOverrides);
    // Refetch when flight, token, or user currency/locale (for booking options) change
    // eslint-disable-next-line react-hooks/exhaustive-deps -- flightOption.id + bookingToken identify the request
  }, [flightOption.id, bookingToken, profile?.preferredCurrency, profile?.locale]);

  if (!bookingToken) {
    return null;
  }

  if (bookingState.isLoading) {
    return <BookingOptionsSkeleton rows={matchedOnly ? 1 : 3} />;
  }

  if (bookingState.error) {
    return (
      <Alert variant="destructive" className="flex items-center gap-2">
        <MaterialIcon icon="airplanemode_inactive" size={IconSizes.sm} />
        <AlertDescription>Unable to load booking options. Please try again later.</AlertDescription>
      </Alert>
    );
  }

  const rawOptions = bookingState.options ?? [];
  if (rawOptions.length === 0) {
    return (
      <Alert className="flex items-center gap-2">
        <MaterialIcon icon="airplanemode_inactive" size={IconSizes.sm} />
        <AlertDescription>No booking options are available for this flight.</AlertDescription>
      </Alert>
    );
  }

  const options: GoogleFlightBookingOptionModel[] = rawOptions.map(
    (data, index): GoogleFlightBookingOptionModel => createGoogleFlightBookingOptionModel(data, index),
  );

  const matchedOption = findClosestBookingMatch(options, flightOption);
  const remainingOptions = matchedOption ? options.filter((opt) => opt !== matchedOption) : options;

  const visibleOptions = showAll ? remainingOptions : remainingOptions.slice(0, INITIAL_BOOKING_OPTIONS_LIMIT);
  const remainingCount = remainingOptions.length - INITIAL_BOOKING_OPTIONS_LIMIT;
  const hasMore = remainingCount > 0 && !matchedOnly;

  // Single-row summary mode — render only the matched option (or the first
  // option if nothing matches the displayed price closely). The flight guide
  // passes `onBookOverride` so the row's Book button routes into the
  // dedicated checkout page rather than the provider tab.
  if (matchedOnly) {
    const summaryOption = matchedOption ?? options[0];
    if (!summaryOption) return null;
    return (
      <div className="rounded-md border border-input bg-background @container">
        <BookingOptionRow
          bookingOption={summaryOption}
          flightOption={flightOption}
          isMatched={true}
          onProviderClicked={onProviderClicked}
          onBookOverride={onBookOverride}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3 @container">
      {matchedOption && (
        <>
          <div className="rounded-md border border-input bg-background">
            <BookingOptionRow
              bookingOption={matchedOption}
              flightOption={flightOption}
              isMatched={true}
              onProviderClicked={onProviderClicked}
            />
          </div>
          {visibleOptions.length > 0 && <div className="font-medium text-sm mt-4">More options</div>}
        </>
      )}

      {visibleOptions.length > 0 && (
        <ul className="rounded-md border bg-background">
          {visibleOptions.map((bookingOption, index) => (
            <BookingOptionRow
              key={bookingOption.id ?? index}
              bookingOption={bookingOption}
              flightOption={flightOption}
              isMatched={matchedOption ? false : true}
              onProviderClicked={onProviderClicked}
            />
          ))}
        </ul>
      )}

      {hasMore && !showAll && (
        <Button variant="link" onClick={() => setShowAll(true)}>
          View {remainingCount} more booking {remainingCount === 1 ? 'option' : 'options'}
        </Button>
      )}
    </div>
  );
}
